import { rankProductMatches } from '../../utils/rankProductMatches';
import MultiProductPicker from '../../components/UI/MultiProductPicker';
import React, { useState, useEffect, useRef } from 'react';
import ProductSourceCheckboxes, { DEFAULT_PRODUCT_TYPES, matchesProductTypes } from '../../components/UI/ProductSourceCheckboxes';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiPlus, FiTrash2, FiUser, FiPackage, FiDollarSign, FiSearch, FiCalendar, FiFileText, FiCreditCard, FiShoppingCart } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { ordenesService } from '../../services/ordenesService';
import { useClientes } from '../../hooks/useClientes';
import { useProductos } from '../../hooks/useProductos';
import { useVendedores } from '../../hooks/useVendedores';
import { clientesService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../components/UI/Notifications/NotificationProvider';
import { toLocalDateString, parseLocalDate, precioParaCategoria } from '../../utils';

const OrdenForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditing = Boolean(id);
  const dropdownRef = useRef(null);

  const { clientes, loadClientes } = useClientes();
  const { productos: allProductos, cachedProductos, searchProductos, isLoading: productosLoading, completedSearchQuery } = useProductos({ salesOnly: true });
  const { vendedores } = useVendedores();

  const [orden, setOrden] = useState({
    client_id: '',
    sale_date: toLocalDateString(),
    delivery_date: '',
    payment_method: 'TERMINOS',
    notes: '',
    internal_notes: '',
    items: [],
    status: 'PENDING_APPROVAL',
    type: 'ORDER',
    user_id: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedClienteData, setSelectedClienteData] = useState(null);

  // Estados para búsqueda de productos
  const [productSearch, setProductSearch] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState(DEFAULT_PRODUCT_TYPES);
  const isNaturalandSource = ['NATURALAND_LAB', 'NATURALANDLAB'].includes(orden.source);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemDiscount, setItemDiscount] = useState(0);

  useEffect(() => {
    loadClientes();
    // Autocompletar vendedor con usuario actual
    if (user && !isEditing) {
      setOrden(prev => ({
        ...prev,
        user_id: user.id || ''
      }));
    }
  }, [user, isEditing]);

  useEffect(() => {
    if (isEditing && id) {
      loadOrden();
    }
  }, [id, isEditing]);

  // Efecto para búsqueda de productos
  useEffect(() => {
    if (productSearch.trim().length > 0) {
      searchProductos(productSearch);
      setSearchResults(rankProductMatches(cachedProductos, productSearch));
      setShowDropdown(true);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [productSearch]);

  // Actualizar resultados cuando cambien los productos
  useEffect(() => {
    if (productSearch.trim().length > 0 && showDropdown) {
      setSearchResults(rankProductMatches(cachedProductos, productSearch));
    }
  }, [cachedProductos, productSearch, showDropdown]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadOrden = async () => {
    setIsLoading(true);
    const result = await ordenesService.getOrdenById(id);
    if (result.success) {
      // Mapear order_type del backend a type del frontend
      const ordenData = {
        ...result.data,
        type: result.data.order_type || result.data.type || 'ORDER',
        // El backend devuelve client_id/user_id planos; usamos el objeto anidado como fallback
        client_id: result.data.client_id ?? result.data.client?.id ?? '',
        user_id: result.data.user_id ?? result.data.user?.id ?? '',
        sale_date: result.data.sale_date
          || (result.data.created_at ? result.data.created_at.split('T')[0] : ''),
        delivery_date: result.data.delivery_date || '',
        payment_method: result.data.payment_method || 'TERMINOS'
      };
      // Mostrar info del cliente (dirección/términos) sin pisar los datos guardados
      if (ordenData.client_id) {
        loadClienteData(ordenData.client_id, false);
      }
      setOrden(ordenData);
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  // Cargar datos del cliente cuando se selecciona.
  // applyDefaults=false al editar: solo muestra los datos del cliente sin pisar
  // el método de pago / fecha de entrega ya guardados en la orden.
  const loadClienteData = async (clienteId, applyDefaults = true) => {
    if (!clienteId) {
      setSelectedClienteData(null);
      return;
    }

    try {
      const result = await clientesService.getCliente(clienteId);

      if (result.success) {
        setSelectedClienteData(result.data);

        if (!applyDefaults) {
          return;
        }

        // Auto-completar método de pago según el cliente
        const metodoPago = result.data.payment_type || 'TERMINOS';

        // Vendedor asignado al cliente: la venta se atribuye a ese vendedor
        // (Sale.user_id), que es quien recibe la comisión. Si el cliente tiene
        // uno asignado, se autoselecciona (sigue siendo editable en el form).
        const vendedorAsignado = result.data.assigned_user_id;

        // Si el cliente tiene términos de pago, calcular fecha de entrega
        setOrden(prev => {
          const updatedOrden = {
            ...prev,
            payment_method: metodoPago
          };

          if (vendedorAsignado) {
            updatedOrden.user_id = vendedorAsignado;
          }

          if (metodoPago === 'TERMINOS' && result.data.payment_terms) {
            const fechaEmision = parseLocalDate(prev.sale_date);
            const diasTerminos = result.data.payment_terms || 30;
            const fechaEntrega = new Date(fechaEmision);
            fechaEntrega.setDate(fechaEntrega.getDate() + diasTerminos);

            updatedOrden.delivery_date = toLocalDateString(fechaEntrega);
          }

          return updatedOrden;
        });
      } else {
        console.error('Error al cargar datos del cliente:', result.message);
      }
    } catch (err) {
      console.error('Error de conexión al cargar cliente:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setOrden(prev => ({
      ...prev,
      [field]: value
    }));

    // Si cambia el cliente, cargar sus datos
    if (field === 'client_id' && value) {
      loadClienteData(value);
    }

    // Limpiar error de validación si existe
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Precio fijo según la categoría del cliente y la Estructura de Precios del
  // producto. El backend lo vuelve a fijar al guardar; aquí es para mostrarlo.
  const categoriaCliente = selectedClienteData?.client_category || null;
  const precioDe = (producto) => precioParaCategoria(producto, categoriaCliente);

  const conPrecio = (item, unitPrice) => {
    const subtotal = item.quantity * unitPrice;
    const descuentoMonto = subtotal * ((item.discount_percentage || 0) / 100);
    return {
      ...item,
      unit_price: unitPrice,
      subtotal,
      discount_amount: descuentoMonto,
      total: subtotal - descuentoMonto
    };
  };

  // Si cambia el cliente con productos ya agregados, se recalcula su precio
  useEffect(() => {
    setOrden(prev => ({
      ...prev,
      items: prev.items.map(it => (it.producto ? conPrecio(it, precioDe(it.producto)) : it))
    }));
  }, [categoriaCliente]); // eslint-disable-line

  const handleProductSelect = (product) => {
    // Cerrar dropdown inmediatamente
    setShowDropdown(false);
    setSearchResults([]);

    // Establecer producto seleccionado
    setSelectedProduct(product);

    // Limpiar campo de búsqueda
    setProductSearch('');
  };

  // Politica de stock de la empresa (Configuracion -> Facturacion). Si permite
  // vender con stock negativo, el faltante no bloquea: solo avisa. Ante cualquier
  // fallo al leerla se asume que no, que es el comportamiento de siempre.
  const { showWarning } = useNotifications();
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  useEffect(() => {
    let activo = true;
    ordenesService.getStockPolicy().then((result) => {
      const d = result?.data;
      const flag = d?.allow_negative_stock_orders ?? d?.data?.allow_negative_stock_orders;
      if (activo) setAllowNegativeStock(flag === true);
    }).catch(() => {});
    return () => { activo = false; };
  }, []);

  const addItemToOrden = () => {
    const qty = parseFloat(itemQuantity);
    if (!selectedProduct || !qty || qty <= 0) {
      setError('Seleccione un producto y especifique una cantidad válida');
      return;
    }

    // Verificar stock disponible. Con la opcion de stock negativo activa se deja
    // anadir igualmente y solo se avisa del faltante.
    if (selectedProduct.stock < qty) {
      if (!allowNegativeStock) {
        setError(`Stock insuficiente. Solo hay ${formatStock(selectedProduct.stock)} unidades disponibles.`);
        return;
      }
      showWarning(`Stock insuficiente: hay ${formatStock(selectedProduct.stock)} y se piden ${formatStock(qty)}. Se añade igualmente porque la configuración permite vender con stock negativo.`);
    }

    const newItem = conPrecio({
      product_id: selectedProduct.id,
      product_name: selectedProduct.nombreProducto,
      product_code: selectedProduct.codigo,
      quantity: qty,
      discount_percentage: itemDiscount,
      // Se guarda para recalcular el precio si después cambia el cliente
      producto: selectedProduct
    }, precioDe(selectedProduct));

    setOrden(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    // Limpiar formulario de producto
    setSelectedProduct(null);
    setProductSearch('');
    setItemQuantity(1);
    setItemDiscount(0);
    setError(null);
  };

  const updateItemQuantity = (index, value) => {
    const quantity = Number(value);
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    setOrden(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? conPrecio({ ...item, quantity }, Number(item.unit_price) || 0) : item
      )
    }));
  };

  const removeItem = (index) => {
    setOrden(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = orden.items.reduce((sum, item) => sum + item.subtotal, 0);
    const descuento = orden.items.reduce((sum, item) => sum + item.discount_amount, 0);
    // El negocio no maneja impuestos
    const total = subtotal - descuento;

    return { subtotal, descuento, total };
  };

  const validateForm = () => {
    const errors = {};

    if (!orden.client_id) {
      errors.client_id = 'Cliente es requerido';
    }
    if (!orden.sale_date) {
      errors.sale_date = 'Fecha es requerida';
    }
    if (!orden.user_id) {
      errors.user_id = 'Vendedor es requerido';
    }
    if (orden.items.length === 0) {
      errors.items = 'Debe agregar al menos un producto';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Por favor corrija los errores en el formulario');
      return;
    }

    setIsLoading(true);
    setError(null);

    const totals = calculateTotals();
    const ordenData = {
      ...orden,
      client_id: parseInt(orden.client_id),
      user_id: parseInt(orden.user_id),
      order_type: orden.type, // Mapear 'type' a 'order_type' para el backend
      items: orden.items.map(({ producto, ...item }) => item),
      subtotal: totals.subtotal,
      discount: totals.descuento,
      total: totals.total,
      // Al editar, conservar la etapa actual; no devolver la orden a Detenidas.
      status: isEditing ? orden.status : 'PENDING_APPROVAL'
    };

    const result = isEditing
      ? await ordenesService.updateOrden(id, ordenData)
      : await ordenesService.createOrden(ordenData);

    if (result.success) {
      navigate('/system/ventas');
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handleCancel = () => {
    navigate('/system/ventas');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatStock = (stock) => {
    // Convertir a string primero para limpiar
    const stockStr = String(stock).trim();

    // Parsear a número
    const num = parseFloat(stockStr);

    // Si no es un número válido, retornar 0
    if (isNaN(num)) return '0';

    // Si es un número entero, mostrar sin decimales
    if (Number.isInteger(num)) {
      return num.toString();
    }

    // Si tiene decimales, redondear a 2 decimales y quitar ceros finales
    const rounded = Math.round(num * 100) / 100;
    const formatted = rounded.toFixed(2).replace(/\.?0+$/, '');

    return formatted;
  };

  const totals = calculateTotals();

  if (isLoading) {
    return <LoadingGrid message={isEditing ? "Cargando orden..." : "Preparando formulario..."} />;
  }

  return (
    <div className="factura-form-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleCancel}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {isEditing ? 'Editar Orden' : 'Nueva Orden'}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handleCancel}>
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            <FiSave size={16} />
            {isEditing ? 'Actualizar' : 'Crear Orden'}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="factura-form">
        {/* Card 1: Información Principal de la Orden - Diseño Premium */}
        <div className="invoice-header-card">
          <div className="invoice-header-card__left">
            <div className="invoice-icon-circle">
              <FiShoppingCart size={32} />
            </div>
            <div className="invoice-header-info">
              <h3 className="invoice-header-title">
                {isEditing ? 'Orden #' + id : 'Nueva Orden'}
              </h3>
              <p className="invoice-header-subtitle">
                Complete los detalles de la orden
              </p>
            </div>
          </div>

          <div className="invoice-header-card__grid">
            {/* Cliente */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiUser size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Cliente *</label>
                <select
                  className={`invoice-field-input ${validationErrors.client_id ? 'invoice-field-input--error' : ''}`}
                  value={orden.client_id}
                  onChange={(e) => handleInputChange('client_id', parseInt(e.target.value) || '')}
                  required
                >
                  <option value="">Seleccionar cliente...</option>
                  {clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombreNegocio}
                    </option>
                  ))}
                </select>
                {validationErrors.client_id && (
                  <span className="invoice-field-error">{validationErrors.client_id}</span>
                )}
                {selectedClienteData && (
                  <>
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: '#f0f9ff',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      color: '#0369a1'
                    }}>
                      <strong>Términos:</strong> {selectedClienteData.payment_type === 'TERMINOS'
                        ? `${selectedClienteData.payment_terms || 30} días`
                        : 'COD (Pago al instante)'}
                      {selectedClienteData.credit_limit > 0 && (
                        <span style={{ marginLeft: '12px' }}>
                          | <strong>Crédito:</strong> ${selectedClienteData.credit_limit?.toLocaleString()}
                        </span>
                      )}
                      <span style={{ marginLeft: '12px' }}>
                        | <strong>Precios:</strong> {categoriaCliente || 'TIENDA'}
                      </span>
                    </div>

                    {/* Dirección de Entrega */}
                    <div style={{
                      marginTop: '8px',
                      padding: '10px 12px',
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      color: '#15803d'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiPackage size={14} />
                        <span>Dirección de Entrega:</span>
                      </div>
                      <div style={{ color: '#166534', lineHeight: '1.4' }}>
                        {selectedClienteData.same_delivery_address || !selectedClienteData.delivery_address ? (
                          // Si usa la misma dirección que el negocio
                          <>
                            {selectedClienteData.business_address && (
                              <div>{selectedClienteData.business_address}{selectedClienteData.business_unit ? `, ${selectedClienteData.business_unit}` : ''}</div>
                            )}
                            {(selectedClienteData.business_city || selectedClienteData.business_state || selectedClienteData.business_postal_code) && (
                              <div>
                                {selectedClienteData.business_city}{selectedClienteData.business_state ? `, ${selectedClienteData.business_state}` : ''} {selectedClienteData.business_postal_code}
                              </div>
                            )}
                            {selectedClienteData.business_country && selectedClienteData.business_country !== 'Estados Unidos' && (
                              <div>{selectedClienteData.business_country}</div>
                            )}
                          </>
                        ) : (
                          // Si tiene dirección de entrega diferente
                          <>
                            {selectedClienteData.delivery_address && (
                              <div>{selectedClienteData.delivery_address}{selectedClienteData.delivery_unit ? `, ${selectedClienteData.delivery_unit}` : ''}</div>
                            )}
                            {(selectedClienteData.delivery_city || selectedClienteData.delivery_state || selectedClienteData.delivery_postal_code) && (
                              <div>
                                {selectedClienteData.delivery_city}{selectedClienteData.delivery_state ? `, ${selectedClienteData.delivery_state}` : ''} {selectedClienteData.delivery_postal_code}
                              </div>
                            )}
                            {selectedClienteData.delivery_country && (
                              <div>{selectedClienteData.delivery_country}</div>
                            )}
                          </>
                        )}
                        {!selectedClienteData.business_address && !selectedClienteData.delivery_address && (
                          <div style={{ fontStyle: 'italic', color: '#9ca3af' }}>No hay dirección registrada</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Fecha */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCalendar size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Orden *</label>
                <input
                  type="date"
                  className={`invoice-field-input ${validationErrors.sale_date ? 'invoice-field-input--error' : ''}`}
                  value={orden.sale_date}
                  onChange={(e) => handleInputChange('sale_date', e.target.value)}
                  required
                />
                {validationErrors.sale_date && (
                  <span className="invoice-field-error">{validationErrors.sale_date}</span>
                )}
              </div>
            </div>

            {/* Fecha Entrega */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCalendar size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Fecha de Entrega</label>
                <input
                  type="date"
                  className="invoice-field-input"
                  value={orden.delivery_date}
                  onChange={(e) => handleInputChange('delivery_date', e.target.value)}
                />
              </div>
            </div>

            {/* Método de Pago */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiCreditCard size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Método de Pago</label>
                <select
                  className="invoice-field-input"
                  value={orden.payment_method}
                  onChange={(e) => handleInputChange('payment_method', e.target.value)}
                >
                  <option value="TERMINOS">Términos</option>
                  <option value="COD">COD (Pago al instante)</option>
                  <option value="TRANSFERENCIA">Transferencia</option>
                  <option value="EFECTIVO">Efectivo</option>
                </select>
              </div>
            </div>

            {/* Tipo de Documento */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Tipo de Documento *</label>
                <select
                  className="invoice-field-input"
                  value={orden.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  required
                >
                  <option value="ORDER">Orden</option>
                  <option value="ESTIMATE">Cotización</option>
                </select>
              </div>
            </div>

            {/* Vendedor */}
            <div className="invoice-field">
              <div className="invoice-field-icon">
                <FiUser size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Vendedor *</label>
                <select
                  className={`invoice-field-input ${validationErrors.user_id ? 'invoice-field-input--error' : ''}`}
                  value={orden.user_id}
                  onChange={(e) => handleInputChange('user_id', parseInt(e.target.value) || '')}
                  required
                >
                  <option value="">Seleccionar vendedor...</option>
                  {vendedores.map(vendedor => (
                    <option key={vendedor.id} value={vendedor.id}>
                      {vendedor.nombre}
                    </option>
                  ))}
                </select>
                {validationErrors.user_id && (
                  <span className="invoice-field-error">{validationErrors.user_id}</span>
                )}
              </div>
            </div>

            {/* Notas - Ocupa 2 columnas */}
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas de la Orden</label>
                <textarea
                  className="invoice-field-input invoice-field-textarea"
                  placeholder="Notas adicionales sobre la orden..."
                  value={orden.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows="2"
                />
                <label className="invoice-field-label">Notas Internas (solo equipo Betel)</label>
                <textarea className="invoice-field-input invoice-field-textarea" value={orden.internal_notes || ''} onChange={(e) => handleInputChange('internal_notes', e.target.value)} rows="2" />
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Tabla de Orden - Diseño Profesional */}
        {orden.items.length > 0 ? (
          <div className="invoice-table-card">
            <div className="invoice-table-header">
              <div className="table-header-icon">
                <FiShoppingCart size={24} />
              </div>
              <div className="table-header-content">
                <h3 className="table-header-title">Productos en la Orden</h3>
                <p className="table-header-subtitle">{orden.items.length} producto(s) agregado(s)</p>
              </div>
            </div>

            <div className="invoice-table-wrapper">
              <table className="invoice-table">
                <thead className="invoice-table-head">
                  <tr>
                    <th className="th-product">Producto</th>
                    <th className="th-qty">Cantidad</th>
                    <th className="th-price">Precio Unit.</th>
                    <th className="th-discount">Desc.</th>
                    <th className="th-total">Total</th>
                    <th className="th-actions"></th>
                  </tr>
                </thead>
                <tbody className="invoice-table-body">
                  {orden.items.map((item, index) => (
                    <tr key={index} className="invoice-table-row">
                      <td className="td-product">
                        <div className="product-cell">
                          <div className="product-cell-icon">
                            <FiPackage size={16} />
                          </div>
                          <div className="product-cell-info">
                            <div className="product-cell-name">{item.product_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="td-qty">
                        <input type="number" min="0.1" step="any" className="qty-badge"
                          aria-label={`Cantidad de ${item.product_name}`}
                          style={{ width: '76px', padding: '7px', border: '1px solid #ad78bb', borderRadius: '8px', color: '#782b8e', background: '#fff' }}
                          value={item.quantity}
                          onChange={(e) => updateItemQuantity(index, e.target.value)} />
                      </td>
                      <td className="td-price">
                        <span className="price-value">{formatCurrency(item.unit_price)}</span>
                      </td>
                      <td className="td-discount">
                        {item.discount_percentage > 0 ? (
                          <span className="discount-badge">{item.discount_percentage}%</span>
                        ) : (
                          <span className="no-discount">—</span>
                        )}
                      </td>
                      <td className="td-total">
                        <span className="total-value">{formatCurrency(item.total)}</span>
                      </td>
                      <td className="td-actions">
                        <button
                          type="button"
                          className="btn-icon-delete"
                          onClick={() => removeItem(index)}
                          title="Eliminar producto"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totales - Diseño Profesional */}
            <div className="invoice-totals-section">
              <div className="totals-container">
                <div className="totals-row">
                  <span className="totals-label">Subtotal</span>
                  <span className="totals-value">{formatCurrency(totals.subtotal)}</span>
                </div>
                {totals.descuento > 0 && (
                  <div className="totals-row totals-row--discount">
                    <span className="totals-label">Descuento</span>
                    <span className="totals-value">-{formatCurrency(totals.descuento)}</span>
                  </div>
                )}
                <div className="totals-divider"></div>
                <div className="totals-row totals-row--final">
                  <span className="totals-label">Total</span>
                  <span className="totals-value">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </div>

            {validationErrors.items && (
              <div className="invoice-table-error">{validationErrors.items}</div>
            )}
          </div>
        ) : (
          <div className="invoice-empty-state">
            <div className="empty-state-icon">
              <FiShoppingCart size={64} />
            </div>
            <h3 className="empty-state-title">No hay productos agregados</h3>
            <p className="empty-state-message">
              Use el buscador de productos para encontrar y agregar ítems a la orden
            </p>
            {validationErrors.items && (
              <div className="invoice-table-error">{validationErrors.items}</div>
            )}
          </div>
        )}
        {/* Card 2: Buscador de Productos - Diseño Moderno */}
        <div className="product-search-card">
          <div className="product-search-header">
            <div className="search-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="search-header-content">
              <h3 className="search-header-title">Agregar Productos</h3>
              <p className="search-header-subtitle">Busque y agregue productos a la orden</p>
            </div>
          </div>

          <div className="product-search-body">
            {!isNaturalandSource && <ProductSourceCheckboxes value={productTypeFilter} onChange={(nextValue) => {
              setProductTypeFilter(nextValue);
              setSearchResults(rankProductMatches(cachedProductos, productSearch));
              setShowDropdown(true);
            }} />}
            {/* Buscador Principal */}
            <div className="search-combo-wrapper" ref={dropdownRef} onKeyDownCapture={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); setShowDropdown(false); e.target.blur?.(); } }}>
              <div className="search-combo-icon-left">
                <FiSearch size={20} />
              </div>
              <input
                type="text"
                className="search-combo-input"
                placeholder="Buscar por nombre de producto o código..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowDropdown(true);
                  }
                }}
              />
              {productosLoading && (
                <div className="search-combo-loading">
                  <div className="spinner-sm"></div>
                </div>
              )}

              {/* Dropdown de resultados con diseño mejorado */}
              {showDropdown && rankProductMatches(cachedProductos, productSearch).length > 0 && (
                <div className="product-dropdown">
                  <MultiProductPicker products={rankProductMatches(cachedProductos, productSearch).filter(product => matchesProductTypes(product, isNaturalandSource ? ['NATURALANDLAB'] : productTypeFilter))} renderMeta={product => <span className="meta-stock">Stock: {formatStock(product.stock)}</span>} renderPrice={product => formatCurrency(precioDe(product))} onAdd={(entries) => {
                    const items = entries.map(({ product, quantity }) => {
                      const unitPrice = precioDe(product);
                      const base = unitPrice * quantity;
                      return {
                        product_id: product.id, product_name: product.nombreProducto, product_code: product.codigo,
                        quantity, quantity_requested: quantity, unit_price: unitPrice,
                        discount_percentage: 0, discount_amount: 0, subtotal: base, total: base,
                        validation_status: 'PENDING', stock_available: product.stock || 0, producto: product
                      };
                    });
                    setOrden(prev => ({ ...prev, items: items.reduce((acc, item) => {
                      const existing = acc.findIndex(x => x.product_id === item.product_id);
                      if (existing < 0) return item.quantity > 0 ? [...acc, item] : acc;
                      const next = [...acc];
                      const old = next[existing];
                      const quantity = Number(old.quantity) + item.quantity;
                      if (quantity <= 0) return next.filter((_, index) => index !== existing);
                      const base = quantity * Number(old.unit_price);
                      next[existing] = { ...old, quantity, quantity_requested: quantity, subtotal: base, total: base };
                      return next;
                    }, prev.items) }));
                    setError(null);
                  }} />
                </div>
              )}
              {showDropdown && !productosLoading && completedSearchQuery === productSearch.trim() && rankProductMatches(cachedProductos, productSearch).filter(product => matchesProductTypes(product, isNaturalandSource ? ['NATURALANDLAB'] : productTypeFilter)).length === 0 && (
                <div className="product-dropdown"><div className="product-dropdown-item">No se encontraron productos relacionados.</div></div>
              )}
            </div>

            {/* Vista previa del producto seleccionado */}
            {selectedProduct && (
              <div className="selected-product-card">
                <div className="selected-product-info">
                  <div className="selected-product-badge">
                    <FiPackage size={16} />
                  </div>
                  <div className="selected-product-details">
                    <h4 className="selected-product-name">{selectedProduct.nombreProducto}</h4>
                    <div className="selected-product-meta">
                      <span className="meta-item">Precio: {formatCurrency(precioDe(selectedProduct))}</span>
                      <span className="meta-separator">•</span>
                      <span className="meta-item">Stock: {formatStock(selectedProduct.stock)}</span>
                    </div>
                  </div>
                </div>

                <div className="selected-product-controls">
                  <div className="control-group">
                    <label className="control-label">Cantidad</label>
                    <input
                      type="number"
                      value={itemQuantity}
                      onChange={(e) => setItemQuantity(e.target.value)}
                      min="0.1"
                      step="0.1"
                      className="control-input"
                    />
                  </div>
                  <div className="control-group">
                    <label className="control-label">Descuento %</label>
                    <input
                      type="number"
                      value={itemDiscount}
                      onChange={(e) => setItemDiscount(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                      step="0.1"
                      className="control-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary btn--add-product"
                    onClick={addItemToOrden}
                  >
                    <FiPlus size={18} />
                    Agregar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Acción inferior equivalente a la del encabezado, al crear o editar. */}
        <div className="orden-bottom-save" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', marginTop: '24px', padding: '20px 0 32px', borderTop: '1px solid #e5e7eb', position: 'relative', zIndex: 1 }}>
          <button type="submit" className="btn btn--primary" disabled={isLoading}>
            <FiSave size={16} /> {isEditing ? 'Actualizar' : 'Crear Orden'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrdenForm;
